/**
 * FormField Component
 *
 * A comprehensive form field wrapper that provides consistent styling,
 * validation, accessibility, and user-friendly error messages.
 * Supports various Material UI input types with enhanced UX patterns.
 */

import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Switch,
  RadioGroup,
  Radio,
  Checkbox,
  Chip,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ReactNode, useState, useEffect, useId } from 'react';
import { z } from 'zod';

export interface FormFieldOption {
  value: string | number | boolean;
  label: string;
  disabled?: boolean;
  description?: string;
}

export interface FormFieldValidation {
  /**
   * Zod schema for validation
   */
  schema?: z.ZodSchema<any>;
  /**
   * Custom validation function
   */
  validate?: (value: any) => string | null;
  /**
   * Whether to validate on change (real-time) or on blur
   */
  validateOn?: 'change' | 'blur' | 'submit';
  /**
   * Whether field is required
   */
  required?: boolean;
}

export interface FormFieldProps {
  /**
   * Field identifier
   */
  name: string;

  /**
   * Field label displayed to user
   */
  label: string;

  /**
   * Current field value
   */
  value: any;

  /**
   * Change handler called when value changes
   */
  onChange: (value: any) => void;

  /**
   * Field type - determines which input component to render
   */
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'textarea'
    | 'select'
    | 'multiselect'
    | 'radio'
    | 'checkbox'
    | 'switch'
    | 'datetime-local'
    | 'url'
    | 'tags'
    | 'file';

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Helper text shown below the field
   */
  helperText?: string;

  /**
   * Options for select, radio, and checkbox fields
   */
  options?: FormFieldOption[];

  /**
   * Validation configuration
   */
  validation?: FormFieldValidation;

  /**
   * Whether the field is disabled
   */
  disabled?: boolean;

  /**
   * Whether the field takes full width
   */
  fullWidth?: boolean;

  /**
   * Size variant for the field
   */
  size?: 'small' | 'medium';

  /**
   * Number of rows for textarea
   */
  rows?: number;

  /**
   * Minimum rows for textarea
   */
  minRows?: number;

  /**
   * Maximum rows for textarea
   */
  maxRows?: number;

  /**
   * Input props to pass to underlying component
   */
  inputProps?: Record<string, any>;

  /**
   * Whether to show character count (for text fields)
   */
  showCharacterCount?: boolean;

  /**
   * Maximum character length
   */
  maxLength?: number;

  /**
   * Success message to show when validation passes
   */
  successMessage?: string;

  /**
   * Custom error message override
   */
  errorMessage?: string;

  /**
   * Whether to show validation status icons
   */
  showValidationIcons?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * For file fields, accepted file types
   */
  accept?: string;

  /**
   * For tags field, maximum number of tags
   */
  maxTags?: number;

  /**
   * Custom field component (advanced usage)
   */
  component?: ReactNode;

  /**
   * Blur handler
   */
  onBlur?: () => void;

  /**
   * Focus handler
   */
  onFocus?: () => void;

  /**
   * Key down handler
   */
  onKeyDown?: (event: React.KeyboardEvent) => void;

  /**
   * Whether the field is required (convenience prop for validation)
   */
  required?: boolean;
}

const FormField = ({
  name,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  helperText,
  options = [],
  validation,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  rows,
  minRows,
  maxRows,
  inputProps,
  showCharacterCount = false,
  maxLength,
  successMessage,
  errorMessage,
  showValidationIcons = true,
  className,
  accept,
  maxTags = 10,
  component,
  onBlur,
  onFocus,
  onKeyDown,
  required,
}: FormFieldProps) => {
  const fieldId = useId();
  const [touched, setTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Determine if field is required
  const isRequired =
    required ||
    validation?.required ||
    (validation?.schema && !validation.schema.isOptional());

  // Validation function
  const validateField = (val: any): string | null => {
    if (validation?.validate) {
      return validation.validate(val);
    }

    if (validation?.schema) {
      try {
        validation.schema.parse(val);
        return null;
      } catch (error) {
        if (error instanceof z.ZodError) {
          return error.errors[0]?.message || 'Invalid value';
        }
        return 'Validation failed';
      }
    }

    if (validation?.required && (!val || (typeof val === 'string' && !val.trim()))) {
      return `${label} is required`;
    }

    return null;
  };

  // Handle validation on change or blur
  const handleValidation = (val: any) => {
    const error = validateField(val);
    setValidationError(error);
    setValidationSuccess(!error && touched && val !== '' && val != null);
  };

  // Handle value changes
  const handleChange = (newValue: any) => {
    onChange(newValue);

    if (validation?.validateOn === 'change' || (!validation?.validateOn && touched)) {
      handleValidation(newValue);
    }
  };

  // Handle blur events
  const handleBlur = () => {
    setTouched(true);
    if (validation?.validateOn === 'blur' || !validation?.validateOn) {
      handleValidation(value);
    }
    onBlur?.();
  };

  // Handle focus events
  const handleFocus = () => {
    onFocus?.();
  };

  // Handle key down events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    onKeyDown?.(event);
  };

  // Effect to validate when value changes externally
  useEffect(() => {
    if (touched && validation) {
      handleValidation(value);
    }
  }, [value, touched]);

  // Determine current error message
  const currentError = errorMessage || validationError;
  const hasError = Boolean(currentError);
  const hasSuccess = validationSuccess && !hasError;

  // Base props common to most field types
  const baseProps = {
    id: fieldId,
    name,
    disabled,
    fullWidth,
    size,
    onBlur: handleBlur,
    onFocus: handleFocus,
    error: hasError,
    className,
  };

  // Helper text with character count
  const getHelperText = () => {
    const parts: string[] = [];

    if (currentError) {
      return currentError;
    }

    if (hasSuccess && successMessage) {
      return successMessage;
    }

    if (helperText) {
      parts.push(helperText);
    }

    if (showCharacterCount && typeof value === 'string' && maxLength) {
      parts.push(`${value.length}/${maxLength} characters`);
    }

    return parts.join(' • ') || undefined;
  };

  // Validation status icon
  const ValidationIcon = () => {
    if (!showValidationIcons || (!hasError && !hasSuccess)) return null;

    if (hasError) {
      return <ErrorIcon color="error" fontSize="small" />;
    }

    if (hasSuccess) {
      return <SuccessIcon color="success" fontSize="small" />;
    }

    return null;
  };

  // Custom component override
  if (component) {
    return (
      <Box className={className}>
        {component}
        {(currentError || (hasSuccess && successMessage)) && (
          <FormHelperText error={hasError}>{getHelperText()}</FormHelperText>
        )}
      </Box>
    );
  }

  // Tags field
  if (type === 'tags') {
    const tags = Array.isArray(value) ? value : [];

    const addTag = () => {
      if (tagInput.trim() && !tags.includes(tagInput.trim()) && tags.length < maxTags) {
        handleChange([...tags, tagInput.trim()]);
        setTagInput('');
      }
    };

    const removeTag = (tagToRemove: string) => {
      handleChange(tags.filter(tag => tag !== tagToRemove));
    };

    return (
      <FormControl {...baseProps} className={className}>
        <Stack spacing={2}>
          <Box>
            <Typography
              component="label"
              htmlFor={fieldId}
              variant="body2"
              fontWeight="medium"
              color={hasError ? 'error' : 'text.primary'}
            >
              {label}
              {isRequired && <span style={{ color: 'error.main' }}> *</span>}
            </Typography>
            {tags.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {tags.map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => removeTag(tag)}
                    size="small"
                    color="primary"
                    disabled={disabled}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder={placeholder || 'Add tag...'}
              size={size}
              disabled={disabled || tags.length >= maxTags}
              InputProps={{
                endAdornment: <ValidationIcon />,
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button
              onClick={addTag}
              disabled={!tagInput.trim() || tags.length >= maxTags || disabled}
              variant="outlined"
              size={size}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Stack>
        </Stack>
        <FormHelperText error={hasError}>{getHelperText()}</FormHelperText>
      </FormControl>
    );
  }

  // Switch field
  if (type === 'switch') {
    return (
      <FormControl className={className}>
        <FormControlLabel
          control={
            <Switch
              {...baseProps}
              checked={Boolean(value)}
              onChange={e => handleChange(e.target.checked)}
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              {label}
              {isRequired && <span style={{ color: 'error.main' }}>*</span>}
              <ValidationIcon />
            </Box>
          }
        />
        <FormHelperText error={hasError}>{getHelperText()}</FormHelperText>
      </FormControl>
    );
  }

  // Radio group
  if (type === 'radio') {
    return (
      <FormControl {...baseProps} className={className}>
        <Typography
          component="legend"
          variant="body2"
          fontWeight="medium"
          color={hasError ? 'error' : 'text.primary'}
          gutterBottom
        >
          {label}
          {isRequired && <span style={{ color: 'error.main' }}> *</span>}
        </Typography>
        <RadioGroup value={value} onChange={e => handleChange(e.target.value)}>
          {options.map(option => (
            <FormControlLabel
              key={String(option.value)}
              value={option.value}
              control={<Radio />}
              label={
                <Box>
                  {option.label}
                  {option.description && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              }
              disabled={disabled || option.disabled}
            />
          ))}
        </RadioGroup>
        <FormHelperText error={hasError}>{getHelperText()}</FormHelperText>
      </FormControl>
    );
  }

  // Select field
  if (type === 'select' || type === 'multiselect') {
    return (
      <FormControl {...baseProps} className={className}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          onChange={e => handleChange(e.target.value)}
          label={label}
          multiple={type === 'multiselect'}
          renderValue={
            type === 'multiselect'
              ? selected => (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {(selected as string[]).map(val => (
                      <Chip key={val} label={val} size="small" />
                    ))}
                  </Box>
                )
              : undefined
          }
          endAdornment={<ValidationIcon />}
        >
          {options.map(option => (
            <MenuItem
              key={String(option.value)}
              value={
                typeof option.value === 'boolean' ? String(option.value) : option.value
              }
              disabled={option.disabled}
            >
              <Box>
                {option.label}
                {option.description && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
        <FormHelperText error={hasError}>{getHelperText()}</FormHelperText>
      </FormControl>
    );
  }

  // Text field (default for most types)
  return (
    <TextField
      {...baseProps}
      label={label}
      value={value}
      onChange={e => handleChange(e.target.value)}
      onKeyDown={handleKeyDown}
      type={type === 'textarea' ? 'text' : type}
      placeholder={placeholder}
      helperText={getHelperText()}
      multiline={type === 'textarea'}
      rows={rows}
      minRows={minRows}
      maxRows={maxRows}
      required={isRequired}
      inputProps={{
        maxLength,
        accept: type === 'file' ? accept : undefined,
        ...inputProps,
      }}
      InputProps={{
        endAdornment: <ValidationIcon />,
      }}
      InputLabelProps={{
        shrink: type === 'datetime-local' || type === 'file' || undefined,
      }}
    />
  );
};

export default FormField;
